import React, { useContext, useState, useEffect, useLayoutEffect } from 'react';
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation
} from 'react-router-dom';
import './translations/i18n';
import { useTranslation } from 'react-i18next';

import { AuthPages, NoAuthPages } from './pages';
import { AuthComponents, NoAuthComponents } from './components';

import ConfigService, { ConfigInterface } from './core/config';
import {
    ConfigContext,
    ProfileContext,
    ProfileContextProvider,
    DataContextProvider
} from './core/contexts';

const ScrollToTop = (props: { children: any }) => {
    const location = useLocation();
    useLayoutEffect(() => {
        document.documentElement.scrollTo(0, 0);
    }, [location.pathname]);
    return props.children;
};

/**
 * Main enter point of the app
 * @returns React.ReactElement
 */
export default function App() {
    const configService = new ConfigService();

    const [config, setConfig] = useState<ConfigInterface>(
        {} as ConfigInterface
    );
    const [isLoadingConfig, setLoadingConfig] = useState(true);

    useEffect(() => {
        if (isLoadingConfig) {
            configService
                .get()
                .then((config: ConfigInterface) => {
                    setConfig(config as ConfigInterface);
                    setLoadingConfig(false);
                })
                .catch((config: ConfigInterface) => {
                    setConfig(config as ConfigInterface);
                    setLoadingConfig(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ConfigContext.Provider value={config}>
            <ProfileContextProvider>
                <Main isLoadingConfig={isLoadingConfig} />
            </ProfileContextProvider>
        </ConfigContext.Provider>
    );
}

/**
 * Handle loading / verified / LoggedInRoutes / LoggedOutRoutes routes
 **/
const Main = (props: { isLoadingConfig: boolean }) => {
    const { user, isLoadingUser, signOut } = useContext(ProfileContext);

    return isLoadingUser || props.isLoadingConfig ? (
        <NoAuthComponents.NoAuthLoader />
    ) : user?.auth ? (
        user.isVerified() ? (
            <LoggedInRoutes />
        ) : (
            <NoAuthPages.NotVerified signOut={signOut} />
        )
    ) : (
        <LoggedOutRoutes />
    );
};

/**
 * Loggued id routes management
 * @returns React.ReactElement
 */
const LoggedInRoutes = (): React.ReactElement => {
    const { t } = useTranslation();

    return (
        <BrowserRouter>
            <Routes>
                <Route
                    path={t('routes.logout')}
                    element={<NoAuthPages.Logout />}
                />
            </Routes>
            <AuthComponents.Header />
            <main>
                <DataContextProvider>
                    <ScrollToTop>
                        <Routes>
                            <Route
                                path=''
                                element={
                                    <Navigate replace to={t('routes.lands')} />
                                }
                            />
                            <Route
                                path={t('routes.home')}
                                element={
                                    <Navigate replace to={t('routes.lands')} />
                                }
                            />
                            <Route
                                path={t('routes.login')}
                                element={
                                    <Navigate replace to={t('routes.lands')} />
                                }
                            />
                            <Route
                                path={t('routes.register')}
                                element={
                                    <Navigate replace to={t('routes.lands')} />
                                }
                            />
                            <Route
                                path={t('routes.lands')}
                                element={<AuthPages.Lands />}
                            />
                            <Route
                                path={t('routes.lands') + '/:landId'}
                                element={<AuthPages.Land />}
                            />
                            <Route
                                path={
                                    t('routes.lands') +
                                    '/:landId' +
                                    t('routes.animals')
                                }
                                element={<AuthPages.Animals />}
                            />
                            <Route
                                path={t('routes.animals')}
                                element={<AuthPages.Animals />}
                            />
                            <Route
                                path={t('routes.animals') + '/:animalId'}
                                element={<AuthPages.Animal />}
                            />
                            <Route
                                path={t('routes.analysis')}
                                element={<AuthPages.Analysis />}
                            />
                            <Route
                                path={t('routes.profile')}
                                element={<AuthPages.Profile />}
                            />
                            <Route
                                path={t('routes.users')}
                                element={<AuthPages.Users />}
                            />
                            <Route
                                path='*'
                                element={<NoAuthPages.NotFound />}
                            />
                        </Routes>
                    </ScrollToTop>
                </DataContextProvider>
            </main>
        </BrowserRouter>
    );
};

/**
 * Loggued out routes management
 * @returns React.ReactElement
 */
const LoggedOutRoutes = (): React.ReactElement => {
    const { t } = useTranslation();

    return (
        <BrowserRouter>
            <Routes>
                <Route
                    path={t('routes.default')}
                    element={<Navigate replace to={t('routes.login')} />}
                ></Route>
                <Route
                    path={t('routes.login')}
                    element={<NoAuthPages.Login />}
                />
                <Route
                    path={t('routes.logout')}
                    element={<Navigate replace to={t('routes.login')} />}
                />
                <Route
                    path={t('routes.register')}
                    element={<NoAuthPages.Register />}
                />
                <Route
                    path={t('routes.password')}
                    element={<NoAuthPages.Password />}
                />
                <Route
                    path='*'
                    element={<Navigate replace to={t('routes.login')} />}
                ></Route>
            </Routes>
        </BrowserRouter>
    );
};
